import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, DownloadIcon, EyeIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock user data
const usersData = [{
  id: 1,
  fullName: 'John Smith',
  username: 'johnsmith',
  email: 'john@example.com',
  phone: '+1 (555) 123-4567',
  status: 'active',
  role: 'Admin',
  region: 'North America',
  lastLogin: '2023-07-15T10:30:00Z'
}, {
  id: 2,
  fullName: 'Jane Doe',
  username: 'janedoe',
  email: 'jane@example.com',
  phone: '+1 (555) 987-6543',
  status: 'active',
  role: 'Manager',
  region: 'Europe',
  lastLogin: '2023-07-16T14:45:00Z'
}, {
  id: 3,
  fullName: 'Robert Johnson',
  username: 'robertj',
  email: 'robert@example.com',
  phone: '+1 (555) 456-7890',
  status: 'inactive',
  role: 'Agent',
  region: 'Asia',
  lastLogin: '2023-07-10T08:15:00Z'
}, {
  id: 4,
  fullName: 'Sarah Williams',
  username: 'sarahw',
  email: 'sarah@example.com',
  phone: '+1 (555) 234-5678',
  status: 'active',
  role: 'Auditor',
  region: 'South America',
  lastLogin: '2023-07-17T09:20:00Z'
}, {
  id: 5,
  fullName: 'Michael Brown',
  username: 'michaelb',
  email: 'michael@example.com',
  phone: '+1 (555) 876-5432',
  status: 'locked',
  role: 'Agent',
  region: 'Africa',
  lastLogin: '2023-07-05T16:30:00Z'
}];
interface User {
  id: number;
  fullName: string;
  username: string;
  email: string;
  phone: string;
  status: string;
  role: string;
  region: string;
  lastLogin: string;
}
const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users] = useState<User[]>(usersData);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const handleAddUser = () => {
    navigate('/users/new');
  };
  const handleEditUser = (user: User) => {
    navigate(`/users/edit/${user.id}`);
  };
  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };
  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };
  const confirmDelete = () => {
    // Delete logic would go here
    setShowDeleteModal(false);
    setSelectedUser(null);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-8022" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-8023" />;
      case 'locked':
        return <StatusBadge status="warning" text="Locked" data-id="element-8024" />;
      default:
        return <StatusBadge status="neutral" text={status} data-id="element-8025" />;
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-8026" />;
      case 'inactive':
      case 'locked':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-8027" />;
      default:
        return null;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  const columns = [{
    header: 'Full Name',
    accessor: 'fullName',
    sortable: true
  }, {
    header: 'Username',
    accessor: 'username',
    sortable: true
  }, {
    header: 'Email',
    accessor: 'email',
    sortable: true
  }, {
    header: 'Role',
    accessor: 'role',
    sortable: true
  }, {
    header: 'Status',
    accessor: (user: User) => getStatusBadge(user.status),
    sortable: false
  }, {
    header: 'Region',
    accessor: 'region',
    sortable: true
  }, {
    header: 'Last Login',
    accessor: (user: User) => formatDate(user.lastLogin),
    sortable: true
  }];
  return <div className="space-y-6" data-id="element-8028">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-8029">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-8030">
          User Management
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-8031">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-8032">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-8033" />
            Filters
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-8034">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-8035" />
            Export
          </button>
          <button onClick={handleAddUser} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-8036">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-8037" />
            Add User
          </button>
        </div>
      </div>
      {showFilters && <Card data-id="element-8038">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-8039">
            <div data-id="element-8040">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-8041">
                Status
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-8042">
                <option value="" data-id="element-8043">All Statuses</option>
                <option value="active" data-id="element-8044">Active</option>
                <option value="inactive" data-id="element-8045">Inactive</option>
                <option value="locked" data-id="element-8046">Locked</option>
              </select>
            </div>
            <div data-id="element-8047">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-8048">
                Role
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-8049">
                <option value="" data-id="element-8050">All Roles</option>
                <option value="Admin" data-id="element-8051">Admin</option>
                <option value="Manager" data-id="element-8052">Manager</option>
                <option value="Agent" data-id="element-8053">Agent</option>
                <option value="Auditor" data-id="element-8054">Auditor</option>
              </select>
            </div>
            <div data-id="element-8055">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-8056">
                Region
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-8057">
                <option value="" data-id="element-8058">All Regions</option>
                <option value="North America" data-id="element-8059">North America</option>
                <option value="Europe" data-id="element-8060">Europe</option>
                <option value="Asia" data-id="element-8061">Asia</option>
                <option value="South America" data-id="element-8062">South America</option>
                <option value="Africa" data-id="element-8063">Africa</option>
                <option value="Oceania" data-id="element-8064">Oceania</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-8065">
            <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-8066">
              Reset
            </button>
            <button className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-8067">
              Apply Filters
            </button>
          </div>
        </Card>}
      <Card data-id="element-8068">
        <Table columns={columns} data={users} keyField="id" actions={user => <div className="flex justify-end space-x-2" data-id="element-8070">
              <button onClick={() => handleViewUser(user)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View" data-id="element-8071">
                <EyeIcon className="h-5 w-5" data-id="element-8072" />
              </button>
              <button onClick={() => handleEditUser(user)} className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="Edit" data-id="element-8073">
                <EditIcon className="h-5 w-5" data-id="element-8074" />
              </button>
              <button onClick={() => handleDeleteUser(user)} className="p-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete" data-id="element-8075">
                <TrashIcon className="h-5 w-5" data-id="element-8076" />
              </button>
            </div>} data-id="element-8069" />
      </Card>
      {/* View User Modal */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="User Details" size="md" data-id="element-8077">
        {selectedUser && <div className="space-y-4" data-id="element-8078">
            <div className="flex items-center" data-id="element-8079">
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-8080">
                {getStatusIcon(selectedUser.status)}
              </div>
              <div data-id="element-8081">
                <h3 className="text-lg font-medium" data-id="element-8082">{selectedUser.fullName}</h3>
                <div className="flex items-center mt-1" data-id="element-8083">
                  {getStatusBadge(selectedUser.status)}
                  <span className="mx-2 text-gray-500 dark:text-gray-400" data-id="element-8084">
                    •
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-8085">
                    {selectedUser.role}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 mt-4" data-id="element-8086">
              <div data-id="element-8087">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-8088">
                  Contact Information
                </h4>
                <div className="mt-2 space-y-2" data-id="element-8089">
                  <p data-id="element-8090">
                    <span className="font-medium" data-id="element-8091">Username:</span>{' '}
                    {selectedUser.username}
                  </p>
                  <p data-id="element-8092">
                    <span className="font-medium" data-id="element-8093">Email:</span>{' '}
                    {selectedUser.email}
                  </p>
                  <p data-id="element-8094">
                    <span className="font-medium" data-id="element-8095">Phone:</span>{' '}
                    {selectedUser.phone}
                  </p>
                </div>
              </div>
              <div data-id="element-8096">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-8097">
                  Account Details
                </h4>
                <div className="mt-2 space-y-2" data-id="element-8098">
                  <p data-id="element-8099">
                    <span className="font-medium" data-id="element-8100">Region:</span>{' '}
                    {selectedUser.region}
                  </p>
                  <p data-id="element-8101">
                    <span className="font-medium" data-id="element-8102">Last Login:</span>{' '}
                    {formatDate(selectedUser.lastLogin)}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-8103">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-8104">
                Quick Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-8105">
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-8106">
                  Reset Password
                </button>
                <button className="px-3 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-md text-sm" data-id="element-8107">
                  Lock Account
                </button>
                <button className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-8108">
                  View Activity
                </button>
              </div>
            </div>
          </div>}
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Deletion" footer={<>
            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-8110">
              Cancel
            </button>
            <button onClick={confirmDelete} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-8111">
              Delete
            </button>
          </>} data-id="element-8109">
        <p className="text-gray-700 dark:text-gray-300" data-id="element-8112">
          Are you sure you want to delete the user "{selectedUser?.fullName}"?
          This action cannot be undone.
        </p>
      </Modal>
    </div>;
};
export default UserList;